/**
 * Project working calendar (DPR reference variable `workingCalender`).
 * Same source portal Daily Targets uses for weekend / holiday day-offs.
 */
import { transactionAxios } from './schedulingApis';

export type WorkingCalendarDayOffs = {
  /** Uppercase weekday names, e.g. "SATURDAY", "SUNDAY" */
  weekOffDays: string[];
  /** year → list of yyyy-MM-dd holiday dates */
  holidays: Record<string, string[]>;
};

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v ?? '').trim()).filter(Boolean);
}

function parseHolidays(raw: unknown): Record<string, string[]> {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string[]> = {};
  for (const [year, dates] of Object.entries(raw as Record<string, unknown>)) {
    const key = String(year).trim();
    if (!key) continue;
    out[key] = asStringArray(dates);
  }
  return out;
}

/** Normalize API payload into a safe dayOffs shape (never throws on missing fields). */
export function parseWorkingCalendarDayOffs(raw: unknown): WorkingCalendarDayOffs {
  const root =
    raw != null && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  // Some payloads nest under `data` again.
  const body =
    root.weekOffDays != null || root.holidays != null || root.week_off_days != null
      ? root
      : root.data != null && typeof root.data === 'object' && !Array.isArray(root.data)
        ? (root.data as Record<string, unknown>)
        : root;

  return {
    weekOffDays: asStringArray(body.weekOffDays ?? body.week_off_days).map((d) =>
      d.toUpperCase(),
    ),
    holidays: parseHolidays(body.holidays),
  };
}

/**
 * GET process-engine/reference/variable/{customerId}/{projectId}/workingCalender
 * (API spelling is `workingCalender`).
 */
export async function fetchWorkingCalendar(params: {
  customerId: string;
  projectId: string;
}): Promise<WorkingCalendarDayOffs | null> {
  const customerId = String(params.customerId ?? '').trim();
  const projectId = String(params.projectId ?? '').trim();
  if (!customerId || !projectId) return null;

  try {
    const res = await transactionAxios.get(
      `process-engine/reference/variable/${encodeURIComponent(customerId)}/${encodeURIComponent(projectId)}/workingCalender`,
    );
    const payload = res.data?.data ?? res.data;
    if (payload == null) return { weekOffDays: [], holidays: {} };
    return parseWorkingCalendarDayOffs(payload);
  } catch (err) {
    console.error(`Error fetching working calendar: ${err}`);
    // Non-fatal: Look ahead remains usable without day-off blocking.
    return null;
  }
}
